<template>
  <div
    class="contentful-tactical-cross-sell-container"
    v-if="data && product"
    :class="{
      'dual-column': isDualColumn,
    }"
  >
    <h3 class="tactical-cross-sell-title">{{ data.tacticalCrossSellTitle }}</h3>
    <div class="row-product">
      <div class="image-container">
        <ProductBadge v-if="product.onSale || product.onClearance" class="badge" :type="product.onSale ? 'sale' : 'clearance'" :size="'small'" />
        <div class="tactical-cross-sell-image">
          <a :href="$router.resolve({ name: 'product', params: { slug: product.slug, id: product.hashKey } }).href" target="_blank">
            <ResponsiveImage :url="`${product.media[0].url}?fit=max&w={120}&q={40}`" :lazy="true" :alt="product.title"></ResponsiveImage>
          </a>
        </div>
      </div>
      <div class="tactical-cross-sell-product-details">
        <div class="tactical-cross-sell-product-title">
          <ProductBadge v-if="product.isNewProduct && !product.onClearance" class="new-badge" :type="'new'" :onImage="false" />
          <a
            class="product-name"
            :href="$router.resolve({ name: 'product', params: { slug: product.slug, id: product.hashKey } }).href"
            target="_blank"
            :aria-label="product.title"
          >
            {{ product.title }}
          </a>
        </div>
        <div class="tactical-cross-sell-price-summary" v-if="isProductAvailableInGeolocation(product)">
          <Price :product="product" :price="formattedPrice" mode="dual-column" />

          <AddToCartButton
            :button-classes="['outlined', 'coral']"
            :scroll-first="false"
            class="add-to-cart-toggle"
            :animate="true"
            :show-loader="true"
            :products="productToCartFormat"
            @added="onAddToCart"
            aria-label="Add to cart"
            :isSmallVariant="true"
          >
            <i class="af af-plus" />
            <i class="af af-cart" />
          </AddToCartButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import Price from './Price.vue';
import { showCartSnackbar } from '@/components/commons/CartSnackbar.vue';
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import useProduct from '@/compositions/useProduct';
import useCommon from '@/compositions/useCommon';
import ProductBadge from '@/components/commons/ProductBadge.vue';
import CountryUtils from '@/components/commons/CountryUtils.js';
import Tracking from '@/components/commons/Tracking.js';
import { formatPrice } from '@/components/commons/MoneyUtils';
import LocationService from '@/services/LocationService';
import { computed } from 'vue';

export default {
  components: {
    AddToCartButton,
    ResponsiveImage,
    ProductBadge,
    Price,
  },
  props: {
    ...contentfulProps,
    isDualColumn: {
      type: Boolean,
      default: false,
    },
  },

  async setup(props, ctx) {
    const { data, init } = useContentful('productPage', props, ctx, { errorPolicy: 'ignore' });

    const countryCode = CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId())?.code ?? CountryUtils.US.code;
    const { currentCountry } = useCommon();

    const { load, product } = useProduct();

    await init();

    if (!data.value) return;

    const productId = computed(() =>
      data?.value?.tacticalCrossSellProduct ? parseInt(data?.value?.tacticalCrossSellProduct.replace(/\D/g, '')) : null,
    );

    if (productId?.value) await load(productId.value);
    const productToCartFormat = computed(() => [
      {
        title: product?.value?.title,
        id: product?.value?.hashKey,
        quantity: product?.value?.packagedItemQuantity ?? 1,
      },
    ]);

    const formattedPrice = computed(() => {
      return formatPrice(product.value, currentCountry.value);
    });

    function onAddToCart(product) {
      showCartSnackbar(product[0]);
      Tracking.track('PDP_TacticalCrossSell', { element: 'add to cart', productId: product[0]?.id });
    }

    function isProductAvailableInGeolocation(product) {
      if (!product.country && !countryCode) {
        return true;
      }
      return product.country && countryCode && product.country.includes(countryCode);
    }

    return {
      data,
      product,
      onAddToCart,
      productToCartFormat,
      countryCode,
      isProductAvailableInGeolocation,
      formattedPrice,
      currentCountry,
    };
  },
};
</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.contentful-tactical-cross-sell-container {
  border: solid 1px var(--grey-light);
  border-radius: var(--space-2);
  padding: 20px 20px 10px 20px;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;

  &:not(.dual-column) {
    @media (min-width: 571px) and (max-width: calc($desktop-breakpoint - 1px)) {
      width: 480px;
      min-height: 130px;
      max-height: 100%;
      margin: auto;
    }
  }
  &.dual-column {
    @media (min-width: 571px) and (max-width: calc($tablet-breakpoint)) {
      width: 480px;
      min-height: 130px;
      max-height: 100%;
    }
  }

  .tactical-cross-sell-title {
    padding-bottom: 16px;
    letter-spacing: normal;
    text-transform: none;

    @media (max-width: 374px) {
      text-align: center;
    }
  }

  .row-product {
    display: flex;
    justify-content: center;
    position: relative;

    @media (max-width: 374px) {
      flex-direction: column;
      align-items: center;
    }

    .image-container {
      position: relative;
    }

    .tactical-cross-sell-product-details {
      display: flex;
      flex: 4;
      flex-direction: column;
      text-align: left;

      .tactical-cross-sell-product-title {
        @media (max-width: 374px) {
          display: flex;
          align-items: flex-start;
        }
      }

      .product-name {
        font-family: var(--font-regular);
        font-weight: normal;
        line-height: var(--line-height-14);
        display: flex;
        padding-bottom: 10px;

        @media (min-width: $tablet-breakpoint) {
          inline-size: 215px;
          overflow: hidden;
        }

        @media (min-width: 520px) and (max-width: calc($tablet-breakpoint)) {
          inline-size: 290px;
          overflow: hidden;
        }

        @media (min-width: 375px) and (max-width: 520px) {
          inline-size: 180px;
          overflow: hidden;
        }

        @media (max-width: 374px) {
          text-align: center;
        }
      }
    }

    a {
      color: var(--dark-grey);
      font-size: var(--font-size-16);
    }

    .tactical-cross-sell-image {
      width: 128px;
      min-height: 66px;
      transition: opacity 100ms ease-out, transform 100ms ease-out;
      opacity: 1;
      padding-right: 10px;

      &:hover {
        transform: scale(1.05);
      }

      @media (min-width: 375px) and (max-width: 410px) {
        padding-right: 0px;
      }

      @media (max-width: 374px) {
        width: 248px;
      }
    }
    .badge {
      z-index: 1;
    }
    .prices {
      font-size: var(--font-size-16);
      line-height: normal;
      padding-top: 12px;
      padding-right: 8px;

      &:not(.mobile) {
        line-height: 0;
      }
    }

    .new-badge {
      margin-top: 1px;
    }

    .tactical-cross-sell-price-summary {
      display: flex;
      justify-content: space-between;

      @media (max-width: 374px) {
        display: flex;
        margin: auto;
      }
    }

    :deep(.add-to-cart-toggle) {
      display: initial;
      @media (max-width: $small-breakpoint) {
        bottom: 0px;
      }

      @media (min-width: $small-breakpoint) {
        bottom: 10px;
      }

      button {
        padding: 4px 10px;
        max-height: 26px;
      }

      i {
        display: inline-block;
        height: 100%;
        width: auto;
        text-align: center;
        vertical-align: middle;
        color: var(--coral-dark);
        font-size: var(--font-size-12);
      }
    }
  }
}
</style>
